import React, { useContext } from 'react';
import { Alert, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostClientDevicesContext from '@totem/components/ubiquiti/hosts/details/clientDevices/ubiquitiHostClientDevicesContext';
import UbiquitiHostDevicesContext from '@totem/components/ubiquiti/hosts/details/devices/ubiquitiHostDevicesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiConnectedClient,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { formatDate, isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const UbiquitiHostClientDevicesTable = () => {
  const { loading, error, clients } = useContext(
    UbiquitiHostClientDevicesContext,
  );
  const { devices } = useContext(UbiquitiHostDevicesContext);

  const getDeviceNameById = (deviceId: string) => {
    const foundDevice = devices.find((chk) => chk.data.id === deviceId);
    if (isNotNull(foundDevice)) {
      return foundDevice.data.name;
    }

    return 'Unknown';
  };

  // Neither the client "type" nor "access.type" enum is documented by the
  // API, so filter options are built from whatever values are actually
  // present in the current data rather than a guessed static list.
  const getValueFilters = (values: string[]) =>
    Array.from(new Set(values))
      .map((value) => ({ text: value, value }))
      .sort((compA, compB) => sortStringAscending(compA.text, compB.text));

  const typeFilters = getValueFilters(
    clients.map((client) => client.data.type),
  );
  const accessFilters = getValueFilters(
    clients.map((client) => client.data.access.type),
  );
  const uplinkDeviceFilters = Array.from(
    new Set(clients.map((client) => client.data.uplinkDeviceId)),
  )
    .map((deviceId) => ({ text: getDeviceNameById(deviceId), value: deviceId }))
    .sort((compA, compB) => sortStringAscending(compA.text, compB.text));

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiConnectedClient>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'Type',
      dataIndex: ['data', 'type'],
      key: 'type',
      render: (_, record: UbiquitiApiDataItem<UbiquitiConnectedClient>) => (
        <Tag>{record.data.type}</Tag>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.type, compB.data.type),
      filters: typeFilters,
      onFilter: (value, record) => record.data.type === value,
    },
    {
      title: 'MAC Address',
      dataIndex: ['data', 'macAddress'],
      key: 'macAddress',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.macAddress, compB.data.macAddress),
    },
    {
      title: 'IP Address',
      dataIndex: ['data', 'ipAddress'],
      key: 'ipAddress',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.ipAddress, compB.data.ipAddress),
    },
    {
      title: 'Access',
      dataIndex: ['data', 'access', 'type'],
      key: 'access',
      render: (_, record: UbiquitiApiDataItem<UbiquitiConnectedClient>) => (
        <Tag>{record.data.access.type}</Tag>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.access.type, compB.data.access.type),
      filters: accessFilters,
      onFilter: (value, record) => record.data.access.type === value,
    },
    {
      title: 'Uplink Device',
      dataIndex: ['data', 'uplinkDeviceId'],
      key: 'uplinkDevice',
      render: (_, record: UbiquitiApiDataItem<UbiquitiConnectedClient>) => (
        <span>{getDeviceNameById(record.data.uplinkDeviceId)}</span>
      ),
      sorter: (compA, compB) =>
        sortStringAscending(
          getDeviceNameById(compA.data.uplinkDeviceId),
          getDeviceNameById(compB.data.uplinkDeviceId),
        ),
      filters: uplinkDeviceFilters,
      onFilter: (value, record) => record.data.uplinkDeviceId === value,
    },
    {
      title: 'Connected At',
      dataIndex: ['data', 'connectedAt'],
      key: 'connectedAt',
      render: (_, record: UbiquitiApiDataItem<UbiquitiConnectedClient>) =>
        formatDate(record.data.connectedAt),
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.connectedAt, compB.data.connectedAt),
    },
  ];

  const exportHeaders = [
    'Name',
    'Type',
    'MAC Address',
    'IP Address',
    'Access',
    'Uplink Device',
    'Connected At',
  ];
  const exportRows = clients.map((client) => [
    client.data.name,
    client.data.type,
    client.data.macAddress,
    client.data.ipAddress,
    client.data.access.type,
    getDeviceNameById(client.data.uplinkDeviceId),
    formatDate(client.data.connectedAt),
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Connected clients unavailable"
        description="No connected client data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiConnectedClient>>
      showSorterTooltip
      columns={columns}
      dataSource={clients}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="connected-clients"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostClientDevicesTable;
